import { useState } from "react";
import { X, Sparkles, RefreshCw, Loader2, Wand2, Send, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";
import { eduError } from "@/lib/eduErrors";

const LANGS = ["es", "fr", "de", "it", "pt", "ja", "en"];
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

const linesToPairs = (text, a, b) =>
  text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [x, ...rest] = l.split("|");
    return { [a]: (x || "").trim(), [b]: rest.join("|").trim() };
  }).filter((p) => p[a]);
const pairsToLines = (arr, a, b) => (arr || []).map((p) => `${p[a]} | ${p[b] || ""}`).join("\n");
const listToText = (arr) => (arr || []).join("\n");
const textToList = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean);

// classId present => class-scoped (can Assign). Otherwise => Library mode (Save only).
// initialMission + missionId => editing an existing library mission (Save = PATCH).
export default function MissionStudio({ classId, className, context, initialMission, missionId, onClose, onAssigned, onSaved }) {
  const { t, locale } = useI18n();
  const [practice, setPractice] = useState("");
  const [lang, setLang] = useState(context?.target_language || "es");
  const [level, setLevel] = useState(context?.level || "A1");
  const [mission, setMission] = useState(initialMission || null);
  const [loading, setLoading] = useState(false);
  const [regen, setRegen] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [assigning, setAssigning] = useState(false);

  const genBody = (action) => (classId
    ? { practice, class_id: classId, action }
    : { practice, target_language: lang, level, action });

  const generate = async () => {
    if (!practice.trim()) { toast.error(t("pm.studio.needPractice")); return; }
    setLoading(true);
    try { setMission((await api.post("/missions/generate", genBody("mission_generation"))).data.mission); }
    catch (e) { toast.error(eduError(e, t, t("pm.studio.genFailed"))); }
    finally { setLoading(false); }
  };

  const regenerate = async (field) => {
    setRegen(field || "all");
    const action = field ? "mission_section_regeneration" : "mission_regeneration";
    try {
      const r = await api.post("/missions/generate", { ...genBody(action), practice: practice || mission.title });
      if (field) setMission((m) => ({ ...m, [field]: r.data.mission[field] }));
      else setMission(r.data.mission);
      toast.success(field ? t("pm.studio.sectionRegen") : t("pm.studio.missionRegen"));
    } catch { toast.error(t("pm.studio.regenFailed")); }
    finally { setRegen(null); }
  };

  const set = (k, v) => setMission((m) => ({ ...m, [k]: v }));

  const assign = async () => {
    setAssigning(true);
    try {
      await api.post("/assignments", { mission, class_ids: [classId], due_date: dueDate || null, practice });
      toast.success(t("pm.studio.assigned"));
      onAssigned?.(); onClose();
    } catch (e) { toast.error(eduError(e, t, t("pm.studio.assignFailed"))); }
    finally { setAssigning(false); }
  };

  const saveDraft = async () => {
    try {
      if (missionId) {
        await api.patch(`/missions/${missionId}`, { patch: mission });
        toast.success(t("pm.studio.updated"));
      } else {
        await api.post("/missions", { mission, practice, class_id: classId || null });
        toast.success(t("pm.studio.saved"));
      }
      onSaved?.();
      if (!classId) onClose();
    } catch (e) { toast.error(eduError(e, t, t("pm.studio.saveFailed"))); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 flex items-start justify-center overflow-auto p-3 sm:p-6" data-testid="mission-studio">
      <div className="bg-paper border-2 border-ink rounded-2xl shadow-hard w-full max-w-2xl my-4">
        <div className="sticky top-0 bg-paper border-b-2 border-ink px-5 py-3 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="font-cabinet font-extrabold text-xl flex items-center gap-2"><Wand2 className="w-5 h-5 text-coral" /> {missionId ? t("pm.studio.editTitle") : t("pm.studio.createTitle")}</h3>
          <button onClick={onClose} className="w-8 h-8 border-2 border-ink rounded-lg flex items-center justify-center bg-white" data-testid="mission-studio-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!mission ? (
            <>
              {classId ? (
                <p className="text-sm text-gray-600">{className} · {context?.target_language?.toUpperCase()} · {context?.level}. {t("pm.studio.classIntro")}</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-bold uppercase text-gray-500 mb-1">{t("pm.studio.language")}</label>
                    <select className="nb-input w-full" value={lang} onChange={(e) => setLang(e.target.value)} data-testid="mission-lang">{LANGS.map((v) => <option key={v} value={v}>{langDisplayName(v, locale)}</option>)}</select></div>
                  <div><label className="block text-xs font-bold uppercase text-gray-500 mb-1">{t("pm.studio.level")}</label>
                    <select className="nb-input w-full" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="mission-level">{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
                </div>
              )}
              <label className="block text-sm font-bold">{t("pm.studio.practiceQ")}</label>
              <textarea className="nb-input w-full h-24" data-testid="mission-practice-input" placeholder={t("pm.studio.practicePlaceholder")} value={practice} onChange={(e) => setPractice(e.target.value)} />
              <button onClick={generate} disabled={loading} className="nb-btn-primary w-full py-3" data-testid="mission-generate-btn">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("pm.studio.designing")}</> : <><Sparkles className="w-5 h-5" /> {t("pm.studio.generateDraft")}</>}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="nb-tag bg-mint text-ink">{mission.target_language?.toUpperCase()} · {mission.level} · {t("pm.studio.minutes", { count: mission.estimated_minutes })}</span>
                <button onClick={() => regenerate(null)} disabled={regen} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="mission-regenerate-all">
                  {regen === "all" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {t("pm.studio.regenAll")}
                </button>
              </div>

              <Field label={t("pm.studio.f.title")}><input dir="auto" className="nb-input w-full font-cabinet font-bold" data-testid="mission-title" value={mission.title || ""} onChange={(e) => set("title", e.target.value)} /></Field>
              <Field label={t("pm.studio.f.intro")}><textarea dir="auto" className="nb-input w-full h-16" value={mission.student_instructions || ""} onChange={(e) => set("student_instructions", e.target.value)} /></Field>

              <RegenField label={t("pm.studio.f.objective")} k="learning_objective" mission={mission} set={set} regen={regen} regenerate={regenerate} />
              <RegenField label={t("pm.studio.f.scenario")} k="scenario" mission={mission} set={set} regen={regen} regenerate={regenerate} />
              <RegenField label={t("pm.studio.f.warmup")} k="warm_up" mission={mission} set={set} regen={regen} regenerate={regenerate} big />
              <RegenField label={t("pm.studio.f.core")} k="core_task" mission={mission} set={set} regen={regen} regenerate={regenerate} big />
              <RegenField label={t("pm.studio.f.challenge")} k="conversation_challenge" mission={mission} set={set} regen={regen} regenerate={regenerate} big />
              <Field label={t("pm.studio.f.mode")}>
                <select className="nb-input w-full" data-testid="mission-conversation-mode"
                  value={mission.conversation_mode || "any"} onChange={(e) => set("conversation_mode", e.target.value)}>
                  <option value="any">{t("pm.studio.f.modeAny")}</option>
                  <option value="voice_encouraged">{t("pm.studio.f.modeVoice")}</option>
                </select>
              </Field>
              <RegenField label={t("pm.studio.f.extension")} k="extension" mission={mission} set={set} regen={regen} regenerate={regenerate} />

              <div className="grid grid-cols-2 gap-3">
                <Field label={t("pm.studio.f.grammar")}><input dir="auto" className="nb-input w-full" value={mission.grammar_focus || ""} onChange={(e) => set("grammar_focus", e.target.value)} /></Field>
                <Field label={t("pm.studio.f.minutes")}><input type="number" className="nb-input w-full" value={mission.estimated_minutes || 12} onChange={(e) => set("estimated_minutes", parseInt(e.target.value) || 12)} /></Field>
              </div>
              <Field label={t("pm.studio.f.culture")}><input dir="auto" className="nb-input w-full" value={mission.cultural_notes || ""} onChange={(e) => set("cultural_notes", e.target.value)} /></Field>

              <Field label={t("pm.studio.f.vocab")}>
                <textarea className="nb-input w-full h-28 font-mono text-sm" value={pairsToLines(mission.target_vocabulary, "term", "translation")} onChange={(e) => set("target_vocabulary", linesToPairs(e.target.value, "term", "translation"))} />
              </Field>
              <Field label={t("pm.studio.f.phrases")}>
                <textarea className="nb-input w-full h-24 font-mono text-sm" value={pairsToLines(mission.useful_phrases, "phrase", "translation")} onChange={(e) => set("useful_phrases", linesToPairs(e.target.value, "phrase", "translation"))} />
              </Field>
              <Field label={t("pm.studio.f.criteria")}>
                <textarea className="nb-input w-full h-20" value={listToText(mission.success_criteria)} onChange={(e) => set("success_criteria", textToList(e.target.value))} />
              </Field>

              {classId && (
                <div className="nb-card bg-sky p-4">
                  <label className="block text-sm font-bold">{t("pm.studio.f.due")}</label>
                  <input type="date" className="nb-input mt-2" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="mission-due-date" />
                </div>
              )}

              <div className="flex gap-2 sticky bottom-0 bg-paper pt-2">
                <button onClick={saveDraft} className="nb-btn-outline py-3 px-4 flex-1" data-testid="mission-save-draft"><Save className="w-4 h-4" /> {missionId ? t("pm.studio.saveChanges") : t("pm.studio.saveLibrary")}</button>
                {classId && (
                  <button onClick={assign} disabled={assigning} className="nb-btn-primary py-3 px-4 flex-1" data-testid="mission-assign-btn">
                    {assigning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 rtl:-scale-x-100" />} {t("pm.studio.assignClass")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function RegenField({ label, k, mission, set, regen, regenerate, big }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">{label}</label>
        <button onClick={() => regenerate(k)} disabled={regen} className="text-xs text-coral font-bold flex items-center gap-1">
          {regen === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {t("pm.studio.regenerate")}
        </button>
      </div>
      <textarea dir="auto" className={`nb-input w-full ${big ? "h-24" : "h-16"}`} value={mission[k] || ""} onChange={(e) => set(k, e.target.value)} />
    </div>
  );
}
